/* eslint-disable @typescript-eslint/camelcase */
import React, {
    useState,
    useEffect,
    useRef,
    ReactNode,
    ReactElement,
    useContext,
} from 'react';
import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';
import {DropdownControlled} from '@homeaway/react-dropdown';
import FilterGroupFooter from '../FilterGroupFooter/FilterGroupFooter';
import {MIN_RESULTS_TO_SEARCH} from '../../utils/constants';
import {FILTER_GROUP_HIDDEN, PRICE_FILTER_SUBMITTED} from '../../utils/edap';

import type {FilterGroupPillProps} from '../FilterGroupPill/FilterGroupPill';

import './FilterGroupDropdown.less';
import {getEventParams} from '../../utils/filters';
export interface FilterGroupDropdownProps extends FilterGroupPillProps {
    filterPill: ReactElement;
    children: ReactNode;
    setIsGroupOpen: (isOpen: boolean) => void;
}

const DROPDOWN_MENU_WIDTH = 408;

export default function FilterGroupDropdown({
    children,
    label,
    secondaryLabel,
    groupId,
    filters,
    results,
    selectedFilters,
    onDismiss,
    onDone,
    onClear,
    filterPill,
    setIsGroupOpen,
    customFiltersParams,
    isMobile,
    scrolling,
}: FilterGroupDropdownProps) {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const FilterDropdownRef = useRef<HTMLDivElement>(null);
    const edapContext: TrackingContextValue = useContext(TrackingContext);

    useEffect(() => {
        if (scrolling) {
            setDropdownOpen(false);
        }
    }, [scrolling]);

    function triggerTotalPriceSubmitted() {
        const {pricesAreNotDefault, eventParams} = getEventParams(
            customFiltersParams
        );
        if (pricesAreNotDefault) {
            edapContext.trigger(PRICE_FILTER_SUBMITTED, eventParams);
        }
    }

    function offset(el: HTMLDivElement) {
        const rect = el.getBoundingClientRect();
        const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;

        return {
            left: rect.left,
            scrollLeft,
            right: rect.right + scrollLeft,
        };
    }

    // Returns true if dropdown should be aligned right
    function determineDropDownPosition() {
        const node = FilterDropdownRef.current;

        if (!isMobile && node) {
            const position = offset(node);
            const dropDownMenu = node.getElementsByClassName(
                'Dropdown__menu'
            )[0] as HTMLElement;

            if (position.left + DROPDOWN_MENU_WIDTH > window.innerWidth) {
                dropDownMenu.style.left = `${
                    position.right - DROPDOWN_MENU_WIDTH
                }px`;
                return true;
            }
            dropDownMenu.style.left = `${position.left}px`;
        }
        return false;
    }

    function handleDropdownClose(action = 'clicked outside') {
        setDropdownOpen(false);

        if (results >= MIN_RESULTS_TO_SEARCH) {
            onDone();
        } else {
            onDismiss();
        }

        edapContext.trigger(FILTER_GROUP_HIDDEN, {
            filtergroupid: groupId,
            action_type: action,
        });
        triggerTotalPriceSubmitted();
    }

    function handleDropdownClick() {
        if (!isDropdownOpen) {
            setDropdownOpen(true);
        } else {
            handleDropdownClose();
        }
    }

    function handleOnBlurOrTap() {
        /* istanbul ignore else */
        if (isDropdownOpen) {
            handleDropdownClose();
        }
    }

    useEffect(() => {
        setIsGroupOpen(isDropdownOpen);
    }, [isDropdownOpen, setIsGroupOpen]);

    return (
        <div className="FilterGroupDropdown" ref={FilterDropdownRef}>
            <DropdownControlled
                id={groupId}
                open={isDropdownOpen}
                customToggle={React.cloneElement(filterPill, {
                    onClick: handleDropdownClick,
                })}
                label={label}
                onDropdownClick={handleDropdownClick}
                onBlur={handleOnBlurOrTap}
                onTouch={handleOnBlurOrTap}
                maxHeight={480}
                dropdownRight={determineDropDownPosition()}
            >
                <div className="FilterGroupDropdown__Container">
                    <span className="FilterGroupDropdown__Label">{label}</span>
                    {secondaryLabel && (
                        <span className="FilterGroupDropdown__SecondaryLabel">
                            {secondaryLabel}
                        </span>
                    )}
                    <div className="FilterGroupDropdown__Content">
                        {children}
                    </div>
                    <FilterGroupFooter
                        groupId={groupId}
                        customFiltersParams={customFiltersParams}
                        onDone={handleDropdownClose}
                        onClear={onClear}
                        results={results}
                        filters={filters}
                        selectedFilters={selectedFilters}
                    />
                </div>
            </DropdownControlled>
        </div>
    );
}

export {FilterGroupDropdown};
